import React from "react"
import { MdAdd, MdDeleteOutline } from "react-icons/md"
import Button from "../Form/Button"
import DatePicker from "../Form/DatePicker"
import Input from "../Form/Input"
import Select from "../Form/Select"
import styles from "./leadFilters.module.css"

const MAX_FILTERS = 10

const commonEmptyOperators = [
    { id: "is_set", value: "Está preenchido" },
    { id: "is_not_set", value: "Não está preenchido" },
]

const stringOperators = [
    { id: "equals", value: "É igual a" },
    { id: "contains", value: "Contém" },
    { id: "starts_with", value: "Começa com" },
    ...commonEmptyOperators,
]

const dateOperators = [
    { id: "equals", value: "Na data" },
    { id: "before", value: "Até a data" },
    { id: "after", value: "A partir da data" },
    { id: "between", value: "Entre as datas" },
    ...commonEmptyOperators,
]

const numberOperators = [
    { id: "equals", value: "É igual a" },
    { id: "gt", value: "Maior que" },
    { id: "gte", value: "Maior ou igual a" },
    { id: "lt", value: "Menor que" },
    { id: "lte", value: "Menor ou igual a" },
    { id: "between", value: "Entre os valores" },
    ...commonEmptyOperators,
]

const multipleSelectionOperators = [
    { id: "any", value: "Possui qualquer uma" },
    { id: "all", value: "Possui todas" },
    { id: "exact", value: "Possui exatamente" },
    { id: "none", value: "Não possui nenhuma" },
    ...commonEmptyOperators,
]

const operatorsByType = {
    text: stringOperators,
    email: stringOperators,
    phone: stringOperators,
    document: stringOperators,
    zipCode: stringOperators,
    currency: [{ id: "equals", value: "É igual a" }, ...commonEmptyOperators],
    date: dateOperators,
    number: numberOperators,
    boolean: [{ id: "equals", value: "É igual a" }, ...commonEmptyOperators],
    select: [{ id: "equals", value: "É igual a" }, ...commonEmptyOperators],
    multi_select: multipleSelectionOperators,
    url: stringOperators,
}

const AdditionalFieldFilters = ({ fields = [], value = [], onChange = () => { } }) => {
    const activeFields = fields.filter(field => field?.is_visible !== false)
    const fieldOptions = activeFields.map(field => ({ id: field._id, value: field.name }))

    const getField = fieldId => activeFields.find(field => field._id === fieldId)

    const updateFilter = (index, data) => {
        onChange(value.map((filter, filterIndex) => filterIndex === index ? { ...filter, ...data } : filter))
    }

    const changeField = (index, fieldId) => {
        const field = getField(fieldId)
        const firstOperator = operatorsByType[field?.type]?.[0]?.id || "equals"
        updateFilter(index, { field_id: fieldId, operator: firstOperator, value: "", value_to: "" })
    }

    const addFilter = () => {
        if (value.length >= MAX_FILTERS) return
        onChange([...value, { field_id: "", operator: "equals", value: "", value_to: "" }])
    }

    const removeFilter = index => {
        onChange(value.filter((_, filterIndex) => filterIndex !== index))
    }

    const renderValue = (filter, field, index) => {
        if (["is_set", "is_not_set"].includes(filter.operator) || !field) return null

        if (field.type === "date") {
            const multiple = filter.operator === "between"
            return (
                <DatePicker
                    name={multiple ? "Período" : "Data"}
                    dateStart={filter.value || ""}
                    dateEnd={filter.value_to || ""}
                    multiple={multiple}
                    onChange={(dateStart, dateEnd) => updateFilter(index, { value: dateStart, value_to: multiple ? dateEnd : "" })}
                />
            )
        }

        const options = (field.options || []).map(option => ({ id: option, value: option }))
        if (field.type === "boolean") {
            return <Select name="Valor" options={[{ id: "true", value: "Sim" }, { id: "false", value: "Não" }]} selected={filter.value ?? ""} onChange={newValue => updateFilter(index, { value: newValue })} multiple={false} />
        }
        if (field.type === "select") {
            return <Select name="Opção" options={options} selected={filter.value || ""} onChange={newValue => updateFilter(index, { value: newValue })} multiple={false} />
        }
        if (field.type === "multi_select") {
            return <Select name="Opções" options={options} selected={Array.isArray(filter.value) ? filter.value : []} onChange={newValue => updateFilter(index, { value: newValue })} multiple={true} showOptionAll={false} />
        }
        if (field.type === "number" && filter.operator === "between") {
            return (
                <>
                    <Input type="number" placeholder="Valor inicial" value={filter.value ?? ""} onChange={event => updateFilter(index, { value: event.target.value })} />
                    <Input type="number" placeholder="Valor final" value={filter.value_to ?? ""} onChange={event => updateFilter(index, { value_to: event.target.value })} />
                </>
            )
        }

        return (
            <Input
                type={field.type === "currency" ? "currency" : (["number", "url"].includes(field.type) ? field.type : "text")}
                placeholder={`Valor de ${field.name}`}
                value={filter.value || ""}
                onChange={event => updateFilter(index, { value: event.target.value })}
            />
        )
    }

    if (!activeFields.length) return null

    return (
        <div className={styles.additionalFilters}>
            {value.map((filter, index) => {
                const field = getField(filter.field_id)
                const operators = operatorsByType[field?.type] || stringOperators
                return (
                    <div className={styles.additionalFilter} key={`${filter.field_id || "new"}-${index}`}>
                        <div className={styles.additionalFilterNumber}>{index + 1}</div>
                        <div className={styles.additionalFilterFields}>
                            <Select name="Campo adicional" placeholder="Selecione um campo" options={fieldOptions} selected={filter.field_id || ""} onChange={fieldId => changeField(index, fieldId)} multiple={false} />
                            <Select name="Condição" placeholder="Selecione uma condição" options={operators} selected={filter.operator || "equals"} onChange={operator => updateFilter(index, { operator, value: "", value_to: "" })} multiple={false} />
                            {renderValue(filter, field, index)}
                        </div>
                        <Button type="button" className={styles.removeFilter} onClick={() => removeFilter(index)} aria-label="Remover filtro">
                            <MdDeleteOutline />
                        </Button>
                    </div>
                )
            })}
            <Button variant="secondary" type="button" onClick={addFilter} disabled={value.length >= MAX_FILTERS}>
                <MdAdd /> Adicionar campo adicional
            </Button>
        </div>
    )
}

export default AdditionalFieldFilters
